import React, { useState, useEffect } from "react";
import moment from "moment";
import { useHistory } from "react-router-dom";
import gql from "graphql-tag";
import { useApolloClient, useQuery } from "react-apollo";
import { Button, Icon, Field, Control } from "rbx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { OrdersTable } from "../../../../components/Orders";
import { UPDATE_ORDER_MUTATION } from "../../../../graphql/Orders/Orders.graphql";
import { ALL_COMPANY_POSTING_PERIODS_QUERY } from "../../../../graphql/CompanyPostingPeriods/CompanyPostingPeriods.graphql";
import Loading from "../../../../components/Loading";
import {
  ourToast as toast,
  runPromisesInSeries,
  uniqueElementsBy
} from "../../../../utils";
import { BILLING_VERIFIED_INVOICE_COLUMNS } from "../columns";
import { useModal } from "../../../../components/ModalProvider";
import { Confirmation } from "../../../../components/modals";
import { useVoidInvoices } from "../../../../hooks";

const ALL_SYSTEM_CODES_QUERY = gql`
  query SystemCodes($where: SystemCodeWhereInput!) {
    SystemCodes(where: $where) {
      RecId
      Type
      Category
      CodeName
      CodeId
      CodeDescription
    }
  }
`;

const ALL_ORDERS_ACCTG_QUERY = gql`
  query ALL_ORDERS_ACCTG_QUERY($where: OrderWhereInput!) {
    Orders(where: $where) {
      RecId
      InvoiceNumber
      InvoiceBatchNumber
      InvoiceDate
    }
  }
`;

const ImportedInAccounting = () => {
  const { setModalOpen } = useModal();

  const SystemCodes = useQuery(ALL_SYSTEM_CODES_QUERY, {
    variables: {
      where: {
        Category_in: ["Order"],
        CodeName_in: ["OrderStatus"],
        CodeId_in: ["INV", "IPA", "BP", "EXA"]
      }
    }
  });
  const postingPeriodData = useQuery(ALL_COMPANY_POSTING_PERIODS_QUERY, {
    variables: {
      where: {
        Open: true
      }
    }
  });
  const client = useApolloClient();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [States, setStates] = useState(null);
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [selectedFlatRows, setSelectedFlatRows] = useState([]);

  const performVoidInvoices = useVoidInvoices();

  useEffect(() => {
    if (SystemCodes?.data?.SystemCodes?.length > 0 && !States) {
      setStates({
        ImportedInAccounting: SystemCodes.data.SystemCodes.find(
          s => s.CodeId === "IPA"
        ),
        BilledAndPosted: SystemCodes.data.SystemCodes.find(
          s => s.CodeId === "BP"
        ),
        InvoiceGenerated: SystemCodes.data.SystemCodes.find(
          s => s.CodeId === "INV"
        ),
        ExportedToAccounting: SystemCodes.data.SystemCodes.find(
          s => s.CodeId === "EXA"
        )
      });
    }
  }, [States, SystemCodes, SystemCodes.data]);

  useEffect(() => {
    if (
      postingPeriodData?.data?.CompanyPostingPeriods?.length > 0 &&
      !currentPeriod
    ) {
      setCurrentPeriod(postingPeriodData.data.CompanyPostingPeriods[0]);
    }
  }, [currentPeriod, postingPeriodData]);

  const performResetToInvoiceGenerated = async selectedData => {
    try {
      if (!selectedData.length) {
        throw Error("Please select some orders to be reset");
      }
      setLoading(true);

      const { data: InvSystemCodeData } = await client.query({
        query: ALL_SYSTEM_CODES_QUERY,
        variables: {
          where: {
            Category: "Order",
            CodeName: "OrderStatus",
            CodeId: "INV"
          }
        }
      });

      const invStatusRecId = InvSystemCodeData?.SystemCodes?.[0];
      if (!invStatusRecId) throw new Error();

      await runPromisesInSeries(
        selectedData.map(({ RecId }, index) => {
          const obj = {
            mutation: UPDATE_ORDER_MUTATION,
            variables: {
              data: {
                StatusRecIdRef: invStatusRecId.RecId
              },
              where: {
                RecId
              }
            }
          };
          if (index === selectedData.length - 1) {
            obj.refetchQueries = ["ALL_ORDERS_VIEW_BY_PAGE_QUERY"];
          }
          return () => client.mutate(obj);
        })
      );

      setLoading(false);
      toast.success("Orders reset successfully");
    } catch (err) {
      toast.error(
        "Whoops! We encountered an error processing this request. Please contact support."
      );
    } finally {
      setSelectedFlatRows([]);
    }
  };

  const handleResetToInvoiceGenerated = async () => {
    try {
      const batchNumbers = [
        ...new Set(selectedFlatRows.map(x => x.InvoiceBatchNumber))
      ];
      const uniqueElements = uniqueElementsBy(batchNumbers, (a, b) => a === b);

      const { data: MatchingOrdersData } = await client.query({
        query: ALL_ORDERS_ACCTG_QUERY,
        variables: {
          where: {
            StatusRecIdRef: States.ImportedInAccounting.RecId,
            InvoiceBatchNumber_in: uniqueElements
          }
        },
        fetchPolicy: "network-only"
      });

      const message = `Do you really want to reset to Invoice Generated all invoices in Batch Number(s) ${batchNumbers.join(
        ", "
      )}?`;

      setModalOpen(
        true,
        <Confirmation
          message={message}
          onCancel={() => {
            setModalOpen(false);
          }}
          onConfirm={async () => {
            await performResetToInvoiceGenerated(MatchingOrdersData.Orders);
            setModalOpen(false);
          }}
        />
      );
    } catch (err) {
      toast.error(
        "Whoops! We encountered an error processing this request. Please contact support."
      );
    }
  };

  const performVoidInvoiceBatch = async selectedData => {
    try {
      if (!selectedData.length) {
        throw Error("Please select some orders to be reset");
      }
      setLoading(true);

      await performVoidInvoices(selectedData);

      toast.success("Orders reseted successfully");
    } catch (err) {
      toast.error(
        "Whoops! We encountered an error processing this request. Please contact support."
      );
    } finally {
      setLoading(false);
      setSelectedFlatRows([]);
    }
  };

  const handleVoidInvoiceBatch = async () => {
    try {
      const batchNumbers = [
        ...new Set(selectedFlatRows.map(x => x.InvoiceBatchNumber))
      ];

      const uniqueElements = uniqueElementsBy(batchNumbers, (a, b) => a === b);
      const { data: MatchingOrdersData } = await client.query({
        query: ALL_ORDERS_ACCTG_QUERY,
        variables: {
          where: {
            StatusRecIdRef: States.ImportedInAccounting.RecId,
            InvoiceBatchNumber_in: uniqueElements
          }
        },
        fetchPolicy: "network-only"
      });

      const message = `Do you really want to void all invoices in Batch Number(s) ${uniqueElements.join(
        ", "
      )}?`;

      setModalOpen(
        true,
        <Confirmation
          message={message}
          onCancel={() => {
            setModalOpen(false);
          }}
          onConfirm={async () => {
            await performVoidInvoiceBatch(MatchingOrdersData.Orders);
            setModalOpen(false);
          }}
        />
      );
    } catch (err) {
      toast.error(
        "Whoops! We encountered an error processing this request. Please contact support."
      );
    }
  };

  const printInvoices = selectedData => {
    if (selectedData.length > 0) {
      const dataToPrint = selectedData.filter(d => d || null);
      const {
        REACT_APP_REPORT_BASE_URL = "https://gdev.gravoc.com"
      } = process.env;
      const BASE_URL = `${REACT_APP_REPORT_BASE_URL}/ReportViewer/ReportViewer.aspx?Path=Invoice`;
      const REPORT_URL = `${BASE_URL}&Param=InvoiceNumber&ParamValue=${dataToPrint.join()}`;
      window.open(REPORT_URL);
      setSelectedFlatRows([]);
    } else {
      toast.error("Please select some invoice");
    }
  };

  const handlePostOrders = async () => {
    try {
      setLoading(true);
      if (!selectedFlatRows.length) {
        throw Error("Please select some orders to be post");
      }

      const batchNumbers = [
        ...new Set(selectedFlatRows.map(x => x.InvoiceBatchNumber))
      ];
      const uniqueElements = uniqueElementsBy(batchNumbers, (a, b) => a === b);

      const { data: MatchingOrdersData } = await client.query({
        query: ALL_ORDERS_ACCTG_QUERY,
        variables: {
          where: {
            StatusRecIdRef: States.ImportedInAccounting.RecId,
            InvoiceBatchNumber_in: uniqueElements,
            InvoiceDate_lt: moment(currentPeriod.PeriodEndDateTime)
              .endOf("day")
              .toISOString(),
            OR: [{ PostFlag: false }, { PostFlag: null }]
          }
        },
        fetchPolicy: "network-only"
      });

      if (MatchingOrdersData.Orders?.length > 0) {
        const message = `Do you really want to post all invoices in Batch Number(s) ${batchNumbers.join(
          ", "
        )}?`;

        setModalOpen(
          true,
          <Confirmation
            message={message}
            onCancel={() => {
              setModalOpen(false);
            }}
            onConfirm={async () => {
              await runPromisesInSeries(
                MatchingOrdersData.Orders.map(({ RecId }, index) => {
                  const obj = {
                    mutation: UPDATE_ORDER_MUTATION,
                    variables: {
                      data: {
                        PostFlag: true,
                        StatusRecIdRef: States.BilledAndPosted.RecId
                      },
                      where: {
                        RecId
                      }
                    }
                  };
                  if (index === MatchingOrdersData.Orders.length - 1) {
                    obj.refetchQueries = ["ALL_ORDERS_VIEW_BY_PAGE_QUERY"];
                  }
                  return () => client.mutate(obj);
                })
              );
              toast.success("Orders posted successfully");
              setModalOpen(false);
            }}
          />
        );
      } else {
        throw new Error("The selected Invoices are not ready to be posted.");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (
    loading ||
    SystemCodes?.loading ||
    !States ||
    postingPeriodData?.loading ||
    !currentPeriod
  ) {
    return <Loading />;
  }
  return (
    <OrdersTable
      callToAction2={handlePostOrders}
      columns={BILLING_VERIFIED_INVOICE_COLUMNS(true)}
      initialSortBy={[
        {
          id: "InvoiceNumber",
          desc: false
        },
        { id: "OrderNumber", desc: false }
      ]}
      pageHeadStart={
        <Field kind="group">
          <Control>
            <Button
              color="info"
              disabled={!selectedFlatRows.length}
              size="small"
              onClick={handleResetToInvoiceGenerated}
            >
              <Icon>
                <FontAwesomeIcon icon="file-export" />
              </Icon>
              <span>Reset to Invoice Generated</span>
            </Button>
          </Control>
          <Control>
            <Button
              color="danger"
              disabled={!selectedFlatRows.length}
              size="small"
              onClick={handleVoidInvoiceBatch}
            >
              <Icon>
                <FontAwesomeIcon icon="exclamation-circle" />
              </Icon>
              <span>Void Invoice Batch</span>
            </Button>
          </Control>
        </Field>
      }
      printInvoices={printInvoices}
      selectedFlatRows={selectedFlatRows}
      setSelectedFlatRows={setSelectedFlatRows}
      where={{
        StatusRecIdRef: States.ImportedInAccounting.RecId
      }}
      onCellClick={cell => {
        if (cell.column.id !== "selection") {
          history.push(`/dispatch/orders/${cell.row.original.RecId}/BOL-ELD`);
        }
      }}
    />
  );
};
ImportedInAccounting.propTypes = {};
export default ImportedInAccounting;
